import { useState, useEffect, useRef, useMemo } from 'react'
import { updateParty } from '../../utils/partyGame'
import { IconClapper, IconCheck, IconWarning, IconBolt } from '../icons'

/**
 * Reaction Test — Co-Op only.
 * Host shows oscillating searchlights, then drops the clapperboard at a random
 * 3–10s point. Players tap a button on their phones; early taps go to the back.
 *
 * Firestore fields used:
 *  - reactionTaps: { [uid]: { tappedAt: number(ms), early: bool, username, avatar } }
 *  - clapperboardAt: number(ms epoch) — set by host when clapperboard drops
 *  - turnOrder: string[] — finalized order, written by host once all players tap
 */
export default function PartyReactionTest({ party, currentUserId, forHost }) {
  const players       = party?.players ?? {}
  const playerEntries = Object.entries(players)
  const playerCount   = playerEntries.length
  const taps          = party?.reactionTaps ?? {}
  const clapperboardAt = party?.clapperboardAt ?? null
  const isClapped      = !!clapperboardAt

  // ── Build ordered queue from taps ─────────────────────────────────────────
  const orderedQueue = useMemo(() => {
    const entries = Object.entries(taps).map(([uid, t]) => ({ uid, ...t }))
    // Valid taps (after clapperboard) sorted by tap time ascending — fastest first
    const valid = entries.filter((t) => !t.early).sort((a, b) => a.tappedAt - b.tappedAt)
    // Early taps in tap-order, placed at the back
    const early = entries.filter((t) =>  t.early).sort((a, b) => a.tappedAt - b.tappedAt)
    return [...valid, ...early]
  }, [taps])

  const allTapped = playerCount > 0 && orderedQueue.length >= playerCount

  // ── Host: schedule the clapperboard drop (3–10s after entering) ───────────
  const clapperScheduledRef = useRef(false)
  useEffect(() => {
    if (!forHost || isClapped || clapperScheduledRef.current || !party?.id) return
    clapperScheduledRef.current = true
    const delayMs = 3000 + Math.random() * 7000
    const id = setTimeout(() => {
      updateParty(party.id, { clapperboardAt: Date.now() }).catch(() => {})
    }, delayMs)
    return () => clearTimeout(id)
  }, [forHost, isClapped, party?.id])

  // ── Host: finalize turn order + actor pickers once everyone has tapped ────
  // New flow: pre-start → reaction_test → actor_pick → reveal → LCA → co-op-playing
  // The reaction test seeds turnOrder AND assigns the two random actor pickers
  // atomically so the next phase (actor_pick) has both fields ready.
  const finalizedRef = useRef(false)
  useEffect(() => {
    if (!forHost || !allTapped || !isClapped) return
    if (finalizedRef.current) return
    if (party?.turnOrder?.length) return
    finalizedRef.current = true
    const turnOrder = orderedQueue.map((t) => t.uid)
    // Randomly pick two players for the blind actor pick
    const allUids   = Object.keys(players)
    const shuffled  = [...allUids].sort(() => Math.random() - 0.5)
    const actorPickerIds = shuffled.slice(0, Math.min(2, shuffled.length))
    updateParty(party.id, {
      turnOrder,
      currentTurnIndex: 0,
      coopChain: [],
      playerMoves: {},
      passedUids: [],
      actorPickerIds,
      status: 'actor_pick',
    }).catch(() => {})
  }, [forHost, allTapped, isClapped, party?.id, party?.turnOrder, orderedQueue, players])

  // ── Player tap handler ────────────────────────────────────────────────────
  const [tapping, setTapping] = useState(false)
  const myTap   = currentUserId ? taps[currentUserId] : null
  const myIndex = orderedQueue.findIndex((t) => t.uid === currentUserId)

  const handleTap = async () => {
    if (tapping || myTap || !currentUserId || !party?.id) return
    setTapping(true)
    const me = players[currentUserId]
    try {
      await updateParty(party.id, {
        [`reactionTaps.${currentUserId}`]: {
          tappedAt: Date.now(),
          early: !isClapped,
          username: me?.username || 'Guest',
          avatar:   me?.avatar   || null,
        },
      })
    } catch {
      setTapping(false)
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  if (forHost) {
    return (
      <div className="reaction-test reaction-test--host">
        <div className="reaction-test__searchlights" aria-hidden="true">
          <div className="reaction-test__beam reaction-test__beam--1" />
          <div className="reaction-test__beam reaction-test__beam--2" />
          <div className="reaction-test__beam reaction-test__beam--3" />
        </div>

        {!isClapped ? (
          <div className="reaction-test__hero">
            <p className="reaction-test__headline">Get Ready...</p>
            <p className="reaction-test__sub">When you see the clapperboard — tap it on your phone!</p>
            <p className="reaction-test__warning"><IconWarning size={15} style={{ verticalAlign: 'middle' }} /> Tap early and you go to the back of the line!</p>
          </div>
        ) : (
          <div className="reaction-test__hero">
            <div className="reaction-test__clapperboard"><IconClapper size={64} /></div>
            <p className="reaction-test__headline">TAP NOW!</p>
          </div>
        )}

        {/* Queue building in real time */}
        {orderedQueue.length > 0 && (
          <div className="reaction-test__queue">
            <p className="reaction-test__queue-title">Turn Order</p>
            <ol className="reaction-test__queue-list">
              {orderedQueue.map((t, i) => (
                <li key={t.uid} className={`reaction-test__queue-item${t.early ? ' reaction-test__queue-item--early' : ''}`}>
                  <span className="reaction-test__queue-rank">#{i + 1}</span>
                  <span className="reaction-test__queue-avatar">
                    {t.avatar ? <img src={t.avatar} alt={t.username} /> : <IconClapper size={18} />}
                  </span>
                  <span className="reaction-test__queue-name">
                    {t.username}{t.early ? ' (early)' : ''}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        <p className="reaction-test__progress">
          {orderedQueue.length}/{playerCount} ready
        </p>
      </div>
    )
  }

  // ── Player phone view ─────────────────────────────────────────────────────
  return (
    <div className="reaction-test reaction-test--player">
      <div className="reaction-test__searchlights reaction-test__searchlights--subtle" aria-hidden="true">
        <div className="reaction-test__beam reaction-test__beam--1" />
        <div className="reaction-test__beam reaction-test__beam--2" />
      </div>

      {myTap ? (
        <div className="reaction-test__locked">
          <p className="reaction-test__locked-check"><IconCheck size={40} /></p>
          <p className="reaction-test__locked-title">You're in!</p>
          <p className="reaction-test__locked-pos">
            Position #{myIndex >= 0 ? myIndex + 1 : '—'}
            {myTap.early ? ' (early)' : ''}
          </p>
          <p className="reaction-test__locked-hint">
            {allTapped ? 'Get ready to play...' : 'Waiting for the others...'}
          </p>
        </div>
      ) : (
        <>
          <p className="reaction-test__player-hint">
            {isClapped ? <><IconClapper size={16} style={{ verticalAlign: 'middle' }} /> GO!</> : 'Watch the host screen...'}
          </p>
          <button
            type="button"
            className={`reaction-test__tap-btn${isClapped ? ' reaction-test__tap-btn--armed' : ''}`}
            onClick={handleTap}
            disabled={tapping}
          >
<IconBolt size={16} style={{ verticalAlign: 'middle' }} /> TAP IT!
          </button>
          {!isClapped && (
            <p className="reaction-test__player-warn">Tap early = back of the line</p>
          )}
        </>
      )}
    </div>
  )
}
